import { useState, type FormEvent } from 'react';
import type { Squad, SquadValidation } from '@fantasy/types';
import { api, ApiRequestError } from '../api.ts';

interface EntryIdImportProps {
  onImported: (squad: Squad, validation: SquadValidation) => void;
}

/**
 * Imports a squad straight from a manager's real FPL entry id — resolved
 * against live FPL data server-side, with no OCR ambiguity to double-check,
 * so submitting saves immediately rather than showing a preview to confirm
 * first (unlike `ScreenshotImport`).
 *
 * One of two forms `ImportSquad` switches between — this component owns
 * just the form content, not the accordion shell around it.
 */
export function EntryIdImport({ onImported }: EntryIdImportProps) {
  const [entryIdInput, setEntryIdInput] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const entryId = Number(entryIdInput);
    if (!Number.isInteger(entryId) || entryId <= 0) return;

    setError(null);
    setImporting(true);
    try {
      const { squad: input } = await api.importFromEntry(entryId);
      const created = await api.createSquad(input);
      onImported(created.squad, created.validation);
      setEntryIdInput('');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not import that entry id.');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="mt-md">
      <p className="hint">
        Enter your FPL entry id (find one in your team URL, e.g.
        fantasy.premierleague.com/entry/<code>2567551</code>/event/1) to pull in this gameweek's
        picks, captain, bench order and chips directly from FPL.
      </p>

      <form className="row row--flush" onSubmit={onSubmit}>
        <input
          type="number"
          aria-label="FPL entry id"
          placeholder="FPL entry id"
          value={entryIdInput}
          onChange={(e) => setEntryIdInput(e.target.value)}
          disabled={importing}
        />
        <button type="submit" className="ghost small" disabled={importing} aria-busy={importing}>
          {importing ? 'Importing…' : 'Import'}
        </button>
      </form>

      {error && (
        <ul className="notices mt-xl">
          <li className="error">{error}</li>
        </ul>
      )}
    </div>
  );
}
